import { apiClient } from './api'

/**
 * Lease agreement service. Backed by server/routes/agreements.js.
 */
export const agreementsService = {
  /** Fetch one agreement (tenant or landlord on the application). */
  async getAgreement(id) {
    const res = await apiClient.get(`/agreements/${id}`)
    return res.agreement
  },

  /** List the authenticated user's agreements. */
  async listAgreements() {
    const res = await apiClient.get('/agreements')
    return res.agreements || []
  },

  /**
   * Record the viewer's signature; returns the updated agreement.
   * `signatureName` is the typed legal name and `esignConsent` the explicit
   * E-SIGN/UETA consent, both stored with the signature on the server.
   * `documentHash` is the hash from fetchPdf: the server refuses the
   * signature (409) if the document changed since it was opened.
   */
  async sign(id, { signatureName, esignConsent, documentHash } = {}) {
    const res = await apiClient.post(`/agreements/${id}/sign`, {
      signatureName,
      esignConsent,
      documentHash,
    })
    return res.agreement
  },

  /**
   * Fetch the lease document a signer is about to sign. Returns the file
   * and the SHA-256 the server computed over those exact bytes (the
   * X-Document-Hash header), which `sign` sends back. The server records
   * the signer's first view of the document on this request.
   */
  async fetchPdf(id) {
    const res = await apiClient.get(`/agreements/${id}/pdf`, {
      responseType: 'blob',
      rawResponse: true,
    })
    const hash =
      (res.headers && typeof res.headers.get === 'function'
        ? res.headers.get('x-document-hash')
        : res.headers?.['x-document-hash']) || null
    return { blob: res.data, hash }
  },

  /** The evidence record (signers, times, hashes) for a lease. */
  async getCertificate(id) {
    const res = await apiClient.get(`/agreements/${id}/certificate.json`)
    return res.certificate
  },

  /**
   * Download the lease and save it via the browser. For an imported lease
   * with the signed copy uploaded, the server sends that file; pass
   * `{ summary: true }` to get Rentra's generated summary of the recorded
   * terms and confirmations instead.
   */
  async downloadPdf(id, { summary = false } = {}) {
    const blob = await apiClient.get(`/agreements/${id}/pdf`, {
      responseType: 'blob',
      params: summary ? { summary: '1' } : undefined,
    })
    const ext = blobExtension(blob?.type)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = summary
      ? `rentra-lease-summary-${id}.pdf`
      : `rentra-lease-${id}.${ext}`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  },

  /**
   * Landlord edits an unsigned lease: rent, deposit, dates, written terms
   * and the late-fee rule. Returns the updated agreement.
   */
  async updateTerms(id, body) {
    const res = await apiClient.put(`/agreements/${id}/terms`, body)
    return res.agreement
  },

  /** Landlord attaches their own lease document to an unsigned lease. */
  async uploadDocument(id, file) {
    const form = new FormData()
    form.append('file', file)
    const res = await apiClient.post(`/agreements/${id}/document`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return res.agreement
  },

  /**
   * Landlord gives notice: the lease ends on moveOutDate, the deposit's
   * refund clock starts, tenants are emailed. `relist` switches the
   * listing back on right away.
   */
  async endLease(id, { moveOutDate, reason, relist }) {
    const res = await apiClient.post(`/agreements/${id}/end`, {
      moveOutDate,
      reason,
      relist,
    })
    // Carry server warnings (e.g. the 180-day retaliation window) with the lease.
    return { ...res.agreement, warnings: res.warnings || [] }
  },

  /** Landlord drafts a renewal for the same household; returns the new lease. */
  async renewLease(id, body) {
    const res = await apiClient.post(`/agreements/${id}/renew`, body)
    return res.agreement
  },

  /**
   * Landlord amends a signed lease mid-term (rent, end date, terms, added
   * roommates); returns the replacement lease awaiting signatures.
   */
  async amendLease(id, body) {
    const res = await apiClient.post(`/agreements/${id}/amend`, body)
    return { ...res.agreement, warnings: res.warnings || [] }
  },

  /**
   * The disclosure pages Rentra would attach to a lease for one of the
   * landlord's listings: { pages: [{ key, title, authority }], draft,
   * unanswered: [fact keys] }.
   */
  async getAddendaPreview(listingId) {
    return apiClient.get('/agreements/addenda-preview', {
      params: { listingId },
    })
  },

  /** A sample of those pages as a PDF blob (no lease yet). */
  async fetchAddendaPreviewPdf(listingId) {
    return apiClient.get('/agreements/addenda-preview.pdf', {
      params: { listingId },
      responseType: 'blob',
    })
  },

  /** Landlord sends an unsigned guarantor a fresh signing link. */
  async resendGuaranty(id, signerId) {
    return apiClient.post(`/agreements/${id}/guarantors/${signerId}/resend`)
  },

  /** Public: what a guarantor's emailed link asks them to sign. */
  async getGuaranty(token) {
    const res = await apiClient.get(`/agreements/guaranty/${token}`)
    return res.guaranty
  },

  /**
   * Public: the document a guarantor signs (the lease with the guaranty
   * page) and the SHA-256 the signature must echo back.
   */
  async fetchGuarantyPdf(token) {
    const res = await apiClient.get(`/agreements/guaranty/${token}/pdf`, {
      responseType: 'blob',
      rawResponse: true,
    })
    const hash =
      (res.headers && typeof res.headers.get === 'function'
        ? res.headers.get('x-document-hash')
        : res.headers?.['x-document-hash']) || null
    return { blob: res.data, hash }
  },

  /**
   * Public: sign the guaranty from the link. body: { signatureName,
   * esignConsent, documentHash, password?, firstName?, lastName?,
   * acceptedTerms? }. Returns { signed, token, user, created }.
   */
  async signGuaranty(token, body) {
    return apiClient.post(`/agreements/guaranty/${token}/sign`, body)
  },

  /**
   * Tenant gives notice to vacate. The lease keeps running until the
   * landlord confirms the move-out; returns the lease with `tenantNotice`.
   */
  async giveNotice(id, { moveOutDate, reason, message }) {
    const res = await apiClient.post(`/agreements/${id}/notice`, {
      moveOutDate,
      reason,
      message,
    })
    return res.agreement
  },
}

const EXT_BY_TYPE = {
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
}

function blobExtension(type) {
  const key = String(type || '')
    .split(';')[0]
    .trim()
    .toLowerCase()
  return EXT_BY_TYPE[key] || 'pdf'
}

export default agreementsService
